/**
 * Movement Lab: the Phase 1 development course.
 *
 * Main route (toward -Z):
 *   hub/start → acceleration corridor + downhill ramp → jump gap over a lethal
 *   pit → curved-wall platform (checkpoint) → narrow passage with lethal walls
 *   and beams → reactive room (traction strips, experiment surfaces) → finish.
 * Side areas: west speed range (ramp + high-speed safe/lethal impact wall) and
 * east experiment chambers A (bending), B (compression), C (coupling).
 *
 * Coordinates: metres, +Y up, the hub floor top is at y = 0.
 */
import type { LevelData, LightDef, SolidDef, TriggerDef } from '../world/LevelData';
import { arcWall, ramp, tiledBox } from '../world/LevelBuilders';

const LOW = -4.25; // floor height after the downhill ramp

const cyl = (id: string, x: number, yBase: number, z: number, radius: number, height: number, material: string, tag?: string): SolidDef => ({
  id,
  shape: { type: 'cylinder', radius, halfHeight: height / 2 },
  position: [x, yBase + height / 2, z],
  material,
  ...(tag ? { tag } : {}),
});

const solids: SolidDef[] = [
  // Hub: large safe movement area around the start.
  ...tiledBox('hub', [0, -0.5, 8], [15, 0.5, 15], 'safe.stone'),

  // Acceleration corridor: flat, then a 12° downhill ramp, then flat again.
  ...tiledBox('corridor.flat', [0, -0.5, -17], [4, 0.5, 10], 'safe.marble'),
  ...ramp('corridor.ramp', [0, 0, -27], [0, LOW, -47], 8, 1, 'safe.marble'),
  ...tiledBox('corridor.low', [0, LOW - 0.5, -54.5], [4, 0.5, 7.5], 'safe.marble'),
  ...tiledBox('corridor.wallL', [-4.5, -0.375, -34.5], [0.5, 4.375, 27.5], 'safe.greenstone'),
  ...tiledBox('corridor.wallR', [4.5, -0.375, -34.5], [0.5, 4.375, 27.5], 'safe.greenstone'),

  // Jump gap (z -62..-67) over a lethal pit.
  ...tiledBox('pit.floor', [0, -14.25, -64.5], [12, 0.25, 6], 'lethal.basic'),

  // Curved-wall platform with a checkpoint.
  ...tiledBox('p2', [0, LOW - 0.5, -82], [12, 0.5, 15], 'safe.stone'),
  ...arcWall('p2.arc', [0, LOW, -82], 9, 135, 225, 12, 4, 0.5, 'safe.marble'),
  cyl('p2.pillarA', 5, LOW, -86, 1, 4, 'safe.greenstone'),
  cyl('p2.pillarB', 8.5, LOW, -76, 0.6, 4, 'safe.greenstone'),
  {
    id: 'p2.angled',
    shape: { type: 'box', halfExtents: [0.3, 2, 3] },
    position: [9, LOW + 2, -90],
    rotationDeg: [0, 35, 0],
    material: 'safe.marble',
  },

  // Narrow passage (3 m wide) with lethal side walls and overhead beams.
  ...tiledBox('narrow.floor', [0, LOW - 0.5, -112], [1.5, 0.5, 15], 'safe.stone'),
  ...tiledBox('narrow.lethalL', [-1.75, LOW + 1.5, -112], [0.25, 1.5, 15], 'lethal.basic', 8, 'lethal-wall'),
  ...tiledBox('narrow.lethalR', [1.75, LOW + 1.5, -112], [0.25, 1.5, 15], 'lethal.basic', 8, 'lethal-wall'),
  { id: 'narrow.beam1', shape: { type: 'box', halfExtents: [1.5, 0.15, 0.3] }, position: [0, LOW + 2.45, -104], material: 'lethal.basic' },
  { id: 'narrow.beam2', shape: { type: 'box', halfExtents: [1.5, 0.15, 0.3] }, position: [0, LOW + 2.45, -119], material: 'lethal.basic' },

  // Reactive room: front half safe, back half traction strips.
  ...tiledBox('room.front', [0, LOW - 0.5, -134.5], [10, 0.5, 7.5], 'safe.stone'),
  ...tiledBox('room.glide', [-6.6667, LOW - 0.5, -149.5], [3.3333, 0.5, 7.5], 'reactive.glide'),
  ...tiledBox('room.safe', [0, LOW - 0.5, -149.5], [3.3333, 0.5, 7.5], 'safe.stone'),
  ...tiledBox('room.grip', [6.6667, LOW - 0.5, -149.5], [3.3333, 0.5, 7.5], 'reactive.grip'),
  ...tiledBox('room.wallL', [-10.5, LOW + 2, -134.5], [0.5, 2, 7.5], 'reactive.compression'),
  ...tiledBox('room.wallR', [10.5, LOW + 2, -134.5], [0.5, 2, 7.5], 'reactive.coupling'),
  cyl('room.bend', 4, LOW, -132, 1, 4, 'reactive.bend'),
  ...tiledBox('room.back', [0, LOW + 2, -157.5], [10, 2.5, 0.5], 'safe.greenstone'),

  // West speed range: climbable ramp from y=10, long flat run, impact wall (safe | lethal).
  { id: 'range.top', shape: { type: 'box', halfExtents: [2.5, 0.5, 5] }, position: [-17.5, 9.5, 5], material: 'safe.stone' },
  ...ramp('range.ramp', [-20, 10, 5], [-50, 0, 5], 10, 1, 'safe.marble'),
  ...tiledBox('range.floor', [-55.5, -0.5, 5], [40.5, 0.5, 10], 'safe.stone'),
  { id: 'range.wallSafe', shape: { type: 'box', halfExtents: [1, 5, 5] }, position: [-97, 4.5, 0], material: 'safe.greenstone', tag: 'speed-wall' },
  { id: 'range.wallLethal', shape: { type: 'box', halfExtents: [1, 5, 5] }, position: [-97, 4.5, 10], material: 'lethal.basic', tag: 'speed-wall-lethal' },

  // East experiment chambers share one floor.
  ...tiledBox('chambers.floor', [35, -0.5, -13.5], [20, 0.5, 36.5], 'safe.stone'),
  // A: geometric bending — pillars and an angled wall.
  cyl('A.p1', 25, 0, 15, 1.2, 6, 'reactive.bend'),
  cyl('A.p2', 33, 0, 9, 1.2, 6, 'reactive.bend'),
  cyl('A.p3', 41, 0, 16, 1.2, 6, 'reactive.bend'),
  cyl('A.p4', 48, 0, 8, 1.2, 6, 'reactive.bend'),
  { id: 'A.wall', shape: { type: 'box', halfExtents: [0.3, 3, 5] }, position: [30, 3, 3], rotationDeg: [0, 30, 0], material: 'reactive.bend' },
  // B: impact compression — facing walls and an angled deflector.
  { id: 'B.wallW', shape: { type: 'box', halfExtents: [0.5, 3, 8] }, position: [21.5, 3, -12], material: 'reactive.compression' },
  { id: 'B.wallE', shape: { type: 'box', halfExtents: [0.5, 3, 8] }, position: [46.5, 3, -12], material: 'reactive.compression' },
  { id: 'B.angled', shape: { type: 'box', halfExtents: [0.4, 3, 3] }, position: [34, 3, -8], rotationDeg: [0, 45, 0], material: 'reactive.compression' },
  // C: surface coupling — a half-buried horizontal cylinder hump and a curved wall.
  {
    id: 'C.hump',
    shape: { type: 'cylinder', radius: 3, halfHeight: 6 },
    position: [35, -1.6, -36],
    rotationDeg: [0, 0, 90],
    material: 'reactive.coupling',
  },
  ...arcWall('C.arc', [35, 0, -38], 9, 20, 160, 12, 4, 0.5, 'reactive.coupling'),
];

const triggers: TriggerDef[] = [
  { id: 'start', kind: 'start', center: [0, 1, 10], halfExtents: [3, 2, 2], material: 'trigger.start' },
  {
    id: 'cp.platform',
    kind: 'checkpoint',
    center: [0, LOW + 1.5, -70],
    halfExtents: [12, 2.5, 0.5],
    material: 'trigger.checkpoint',
    checkpointIndex: 0,
    spawn: { position: [0, LOW + 0.02, -71.5], yawDeg: 0 },
  },
  {
    id: 'cp.room',
    kind: 'checkpoint',
    center: [0, LOW + 1.5, -128.5],
    halfExtents: [10, 2.5, 0.5],
    material: 'trigger.checkpoint',
    checkpointIndex: 1,
    spawn: { position: [0, LOW + 0.02, -130], yawDeg: 0 },
  },
  { id: 'finish', kind: 'finish', center: [0, LOW + 1.5, -155], halfExtents: [10, 2.5, 0.5], material: 'trigger.finish' },
];

const lantern = (x: number, y: number, z: number): LightDef => ({ position: [x, y, z], color: 0xffa64d, intensity: 18, distance: 22 });

export const MOVEMENT_LAB: LevelData = {
  id: 'movement-lab',
  name: 'Movement Lab',
  version: 1,
  solids,
  triggers,
  start: { position: [0, 0.02, 12], yawDeg: 0 },
  killPlaneY: -30,
  lights: [
    lantern(0, 5, 8),
    lantern(0, 4, -20),
    lantern(0, 1, -50),
    lantern(-6, 0, -80),
    lantern(0, 0, -112),
    lantern(0, 0, -140),
    lantern(-40, 6, 5),
    lantern(35, 6, 10),
    lantern(35, 6, -12),
    lantern(35, 6, -36),
  ],
  labels: [
    { text: 'A · BENDING', position: [36, 7, 20] },
    { text: 'B · COMPRESSION', position: [34, 7, -3] },
    { text: 'C · COUPLING', position: [35, 7, -28] },
    { text: 'SPEED RANGE', position: [-40, 9, 5] },
  ],
};
